import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { CountryRegion } from "@mapmycustomers/shared/enum";
import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import { Territory } from "@mapmycustomers/shared/types/entity";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { CreateTerritoryWizard } from "@app/component/createEditEntity/Territory";
import NoResults from "@app/scene/map/components/Toolbar/components/TerritoriesPopover/NoResults";
import {
  enterTerritoryMode,
  fetchTerritories,
  setBoundaryType,
  toggleLayerVisibility,
} from "@app/scene/map/store/actions";
import {
  areTerritoriesLoading,
  getTerritories,
  getTerritoriesToolbarFilters,
  getTerritoriesTotal,
} from "@app/scene/map/store/selectors";
import { isCurrentUserMember, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import Filter from "./Filter";
import Header from "./Header";
import messages from "./messages";
import styles from "./TerritoriesTab.module.scss";
import TerritoryListItem from "./TerritoryListItem";

interface Props {
  canCreateTerritories?: boolean;
  filters: {
    countryRegions?: CountryRegion[];
    creatorIds?: User["id"][];
    query?: string;
    sharedWithIds?: User["id"][];
  };
  loading?: boolean;
  onClick?: (territory: Territory) => void;
  onEnterTerritoryMode?: typeof enterTerritoryMode;
  onFetchTerritories: typeof fetchTerritories.request;
  onHide?: () => void;
  onToggleLayerVisibility: typeof toggleLayerVisibility;
  setBoundaryType: typeof setBoundaryType;
  territories: Territory[];
  totalTerritories: number;
}

const TerritoriesTab: React.FC<Props> = ({
  canCreateTerritories,
  filters,
  loading,
  onClick,
  onEnterTerritoryMode,
  onFetchTerritories,
  onHide,
  onToggleLayerVisibility,
  setBoundaryType,
  territories,
  totalTerritories,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [query, setQuery] = useState(filters.query ?? "");
  const [creatorIds, setCreatorIds] = useState<User["id"][]>(filters.creatorIds ?? []);
  const [sharedWithIds, setSharedWithIds] = useState<User["id"][]>(filters.sharedWithIds ?? []);
  const [regions, setRegions] = useState<CountryRegion[]>(filters.countryRegions ?? []);

  const [createWizardVisible, showCreateWizard, hideCreateWizard] = useBoolean();
  const handleCreateTerritory = useCallback(() => {
    onHide?.();
    showCreateWizard();
  }, [showCreateWizard, onHide]);

  const handleCreateSmartTerritory = useCallback(
    (areaType: AreaType) => {
      analytics.clicked(["Create new", "Smart"], { areaType });
      onEnterTerritoryMode?.({ areaType, territoryId: "create" });
    },
    [analytics, onEnterTerritoryMode]
  );

  const handleDrawTerritory = useCallback(() => {
    analytics.clicked(["Create new", "Draw"]);
    onEnterTerritoryMode?.({ territoryId: "create" });
  }, [analytics, onEnterTerritoryMode]);

  const handleImportTerritory = useCallback(() => {
    analytics.clicked(["Create new", "Import"]);
    throw new Error("Not supported yet");
  }, [analytics]);

  const handleHideWizard = useCallback(
    (finished: boolean) => {
      if (finished) {
        onToggleLayerVisibility({ name: "boundaries", visible: true });
        setBoundaryType(BoundaryType.TERRITORY);
      }
      hideCreateWizard();
    },
    [hideCreateWizard, onToggleLayerVisibility, setBoundaryType]
  );

  useEffect(() => {
    onFetchTerritories({ countryRegions: regions, creatorIds, query, sharedWithIds });
  }, [creatorIds, onFetchTerritories, query, sharedWithIds, regions]);

  const handleClearFilters = useCallback(() => {
    setQuery("");
    setCreatorIds([]);
    setRegions([]);
    setSharedWithIds([]);
  }, []);

  return (
    <div className={styles.container}>
      {totalTerritories === 0 ? (
        loading ? (
          <LoadingSpinner />
        ) : (
          <NoResults
            action={intl.formatMessage(messages.noTerritoriesAction)}
            description={intl.formatMessage(messages.noTerritoriesDescription)}
            onAction={canCreateTerritories ? handleCreateTerritory : undefined}
            title={intl.formatMessage(messages.noTerritoriesTitle)}
          />
        )
      ) : (
        <>
          <Header
            canCreateTerritories={canCreateTerritories}
            onCreateTerritory={handleCreateTerritory}
            territories={territories}
            total={totalTerritories}
          />
          <div className={styles.content}>
            <Filter
              creatorIds={creatorIds}
              onCreatorsChange={setCreatorIds}
              onQueryChange={setQuery}
              onRegionsChange={setRegions}
              onSharedWithChange={setSharedWithIds}
              query={query}
              regions={regions}
              sharedWithIds={sharedWithIds}
            />
            <div className={styles.list}>
              {territories.map((territory) => (
                <TerritoryListItem key={territory.id} onClick={onClick} territory={territory} />
              ))}
              {!territories.length && (
                <NoResults
                  action={intl.formatMessage(messages.noResultsClear)}
                  description={intl.formatMessage(messages.noResultsDescription)}
                  onAction={handleClearFilters}
                  title={intl.formatMessage(messages.noResultsTitle)}
                />
              )}
            </div>
          </div>
        </>
      )}
      {createWizardVisible && (
        <CreateTerritoryWizard
          onCreateSmartTerritory={handleCreateSmartTerritory}
          onDrawTerritory={handleDrawTerritory}
          onHide={handleHideWizard}
          onImportTerritory={handleImportTerritory}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canCreateTerritories: !isCurrentUserMember(state) && !isCurrentUserReadOnly(state),
  filters: getTerritoriesToolbarFilters(state),
  loading: areTerritoriesLoading(state),
  territories: getTerritories(state),
  totalTerritories: getTerritoriesTotal(state),
});

const mapDispatchToProps = {
  onEnterTerritoryMode: enterTerritoryMode,
  onFetchTerritories: fetchTerritories.request,
  onToggleLayerVisibility: toggleLayerVisibility,
  setBoundaryType,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerritoriesTab);
